import { CSSProperties, ReactNode } from "react";
import { Button, Flex, Typography } from "antd";
import { ArrowRightOutlined, ClockCircleFilled, ExclamationCircleFilled, InfoCircleFilled } from "@ant-design/icons";
import { colors } from "shared/theme/color";
import { textPx } from "shared/constants/zoom";

// The one note shape in the admin (born in the payroll flow, 2026-10-13;
// moved here 2026-10-17 when the client package card took it up). Built like
// the page's own metric cards: an icon on a soft tile at the left, a bold
// title line, a quieter body line under it, and the action on the right. The
// title is the FACT and the body the ACTION it calls for; two facts that
// share an action are passed as one item, so the action is printed once. Not
// AntD's filled Alert, and not bare coloured text (which read as a caption
// and was skimmed past). The box hugs its content and wraps at maxWidth.
export type WarningItem = { fact: string | string[]; action?: string };

// Three tones, the badge rule's: amber is "needs a hand" (a draft to check
// before finalizing, a refused generate), navy is "in flight or a neutral
// fact" (a frequency change queued or just started), grey is "closed, nothing
// more to do" (the record of a voided sale or an upgrade under a package
// card, the way a refund sits in a Stripe or Shopify record's history). Same
// structure either way, so they never look like different kinds of thing.
export type WarningTone = "warning" | "info" | "neutral";
type IconComponent = typeof ExclamationCircleFilled;
const TONES: Record<WarningTone, { bg: string; border: string; tile: string; icon: string; Icon: IconComponent }> = {
  warning: { bg: colors.amberTint, border: "rgba(239, 159, 39, 0.32)", tile: "rgba(239, 159, 39, 0.18)", icon: colors.amberDark, Icon: ExclamationCircleFilled },
  info: { bg: colors.navyTint, border: "rgba(46, 63, 139, 0.22)", tile: "rgba(46, 63, 139, 0.12)", icon: colors.navy, Icon: ClockCircleFilled },
  neutral: { bg: colors.card, border: colors.line, tile: colors.bgSoft, icon: colors.ink2, Icon: InfoCircleFilled },
};

const WarningBox = ({
  items,
  extra,
  style,
  tone = "warning",
  icon,
  accent,
}: {
  items: WarningItem[];
  extra?: ReactNode;
  style?: CSSProperties;
  tone?: WarningTone;
  /** Replaces the tone's icon when the note is about one kind of event
   *  (a stop sign on a voided sale, an arrow on an upgrade). */
  icon?: IconComponent;
  /** Colours the icon and its tile only, leaving the box neutral: a
   *  record wears the colour of the status it records (the badge's red on
   *  a voided sale, navy on an upgrade), so the eye pairs the tile with
   *  the chip in the head (chris, 2026-10-17). */
  accent?: { icon: string; tile: string };
}) => {
  const t = TONES[tone];
  const Icon = icon ?? t.Icon;
  const tileBg = accent?.tile ?? t.tile;
  const iconColor = accent?.icon ?? t.icon;
  return (
  <Flex
    align="center"
    gap={14}
    style={{
      width: "fit-content",
      maxWidth: 820,
      padding: "12px 16px 12px 12px",
      background: t.bg,
      border: `1px solid ${t.border}`,
      borderRadius: 12,
      ...style,
    }}
  >
    <Flex
      align="center"
      justify="center"
      style={{ width: 34, height: 34, borderRadius: 10, background: tileBg, flexShrink: 0, alignSelf: "flex-start" }}
    >
      <Icon style={{ fontSize: textPx(16), color: iconColor }} />
    </Flex>
    <Flex vertical gap={10} style={{ flex: 1, minWidth: 0 }}>
      {items.map((item, index) => {
        const facts = Array.isArray(item.fact) ? item.fact : [item.fact];
        return (
          <Flex key={index} vertical gap={2}>
            {facts.map((fact) => (
              <Typography.Text key={fact} style={{ fontSize: textPx(13.5), fontWeight: 600, color: colors.ink, lineHeight: 1.4 }}>
                {fact}
              </Typography.Text>
            ))}
            {item.action && (
              <Typography.Text style={{ fontSize: textPx(13), color: colors.ink2, lineHeight: 1.45, whiteSpace: "pre-wrap" }}>
                {item.action}
              </Typography.Text>
            )}
          </Flex>
        );
      })}
    </Flex>
    {extra && <div style={{ marginLeft: 10, flexShrink: 0 }}>{extra}</div>}
  </Flex>
  );
};

// The control beside a note: the same small white button whichever the
// action, so the amber banners and the navy settings note read as one
// family (a navy text link on the amber tint looked unfinished beside the
// settings card's button). `arrow` marks the ones that only navigate.
export const WarningLink = ({ children, onClick, arrow = true }: { children: ReactNode; onClick: () => void; arrow?: boolean }) => (
  <Button size="small" style={{ whiteSpace: "nowrap" }} onClick={onClick}>
    {children}
    {arrow && <ArrowRightOutlined style={{ fontSize: textPx(11), marginLeft: 2 }} />}
  </Button>
);

export default WarningBox;
